import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AGENT_INTERCEPTION_CONFIRM_CONTENT } from '../../constants';
import { AIStatusColors } from '../../redux/features/ai/ai.constants';
import {
  selectAIStatus,
  selectIsAIAutoMode,
  selectIsAIEscalation,
} from '../../redux/features/ai/ai.selector';
import {
  isInterceptionIgnoredEvent,
  markInterceptionPrompted,
} from '../../utils/agentInterception';
import ConfirmDialog from '../common/ConfirmDialog';

/**
 * AGENT INTERCEPTION CONFIRM (HITL 3.0).
 *
 * Headless guard for an agent stepping into a live auto-AI order: while the AI is
 * driving smoothly (auto mode + green status + no escalation), the first
 * keydown/mousedown that isn't a chrome interaction (header, account menu,
 * layout-preference dialog, pane resize handles) opens an "are you sure you want
 * to take over?" ConfirmDialog. Once confirmed it stops asking for the rest of
 * this auto-AI window.
 *
 * The legacy layout gets this behaviour from <AIStatusBar/>. The HITL 3.0 layout
 * replaced AIStatusBar with the display-only Pulse status band, so this component
 * re-mounts the same guard in MainPageLayout (which Body renders only under
 * hitl_3_0) — otherwise agents would take over auto-AI orders with no prompt.
 * Kept in lockstep with AIStatusBar's dialog logic.
 */
interface IInterceptionConfirmState {
  open: boolean;
  confirm: boolean;
}

const AgentInterceptionConfirm = () => {
  const isAIAutoMode = useSelector(selectIsAIAutoMode);
  const isAIEscalation = useSelector(selectIsAIEscalation);
  const aiStatus = useSelector(selectAIStatus);

  const confirmAgentInterception =
    isAIAutoMode && aiStatus.status === AIStatusColors.green && !isAIEscalation;
  const [dialog, setDialog] = useState<IInterceptionConfirmState>({
    open: false,
    confirm: false,
  });

  useEffect(() => {
    if (!confirmAgentInterception || dialog.confirm) {
      return undefined;
    }
    const handleKeyboardMouseEvent = (event: Event) => {
      if (isInterceptionIgnoredEvent(event)) {
        return;
      }
      // Recorded so a keyboard shortcut acting on this same event can stay silent —
      // a mouse press gets that from the backdrop, a keydown cannot.
      markInterceptionPrompted(event);
      setDialog((prevState) => ({ ...prevState, open: true }));
    };
    document.addEventListener('keydown', handleKeyboardMouseEvent, true);
    document.addEventListener('mousedown', handleKeyboardMouseEvent, true);
    return () => {
      document.removeEventListener('keydown', handleKeyboardMouseEvent, true);
      document.removeEventListener('mousedown', handleKeyboardMouseEvent, true);
    };
  }, [confirmAgentInterception, dialog]);

  // Re-arm the one-shot guard once the AI leaves the confirmable window — the
  // agent intercepted (isAIAutoMode → false), the AI escalated, or status went
  // non-green. Without this the `confirm` latch would stay set for the whole
  // life of the layout, so the prompt would fire for the first intercepted order
  // and NEVER again for any later car. Resetting here means that when the AI next
  // re-enters auto mode (e.g. the next car after a finish/session reset), the
  // guard is armed afresh and the first take-over prompts again. Safe against the
  // open dialog: at first mousedown `confirm` is still false (the underlying
  // action flips isAIAutoMode without preventDefault), so this only fires AFTER
  // the agent has confirmed, by which point the listeners are already detached.
  useEffect(() => {
    if (!confirmAgentInterception && dialog.confirm) {
      setDialog({ open: false, confirm: false });
    }
  }, [confirmAgentInterception, dialog.confirm]);

  return (
    <ConfirmDialog
      open={dialog.open}
      content={AGENT_INTERCEPTION_CONFIRM_CONTENT}
      onClose={() => setDialog((prevState) => ({ ...prevState, open: false }))}
      onConfirm={() => setDialog({ open: false, confirm: true })}
      closeBtnText={'No'}
      confirmBtnText={'Yes'}
    />
  );
};

export default AgentInterceptionConfirm;
